import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeMarketingHtml, sanitizeStyleBlock } from '../utils/sanitizeHtml';

/**
 * Features marketing page.
 * Source of truth for the marketing HTML lives at /public/site/features.html.
 * We fetch it once, inject the <style> + <body> into the React tree and then
 * re-attach the inline behaviour (reveal-on-scroll observer + module-nav
 * active-state observer) and intercept internal <a> clicks for SPA navigation.
 */
export default function Features() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [styleHtml, setStyleHtml] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  // Inject Google Fonts <link> once at document head (Ubuntu + DM Sans).
  useEffect(() => {
    const id = 'ludy-features-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap';
    document.head.appendChild(link);
  }, []);

  // Fetch the marketing HTML once.
  useEffect(() => {
    let cancelled = false;
    fetch('/site/features.html')
      .then(res => res.text())
      .then(html => {
        if (cancelled) return;
        const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/);
        const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/);
        const body = (bodyMatch?.[1] || '').replace(/<script[\s\S]*?<\/script>/g, '');
        setStyleHtml(styleMatch?.[1] || '');
        setBodyHtml(body);
        // top-of-page on first render
        window.scrollTo(0, 0);
      });
    return () => { cancelled = true; };
  }, []);

  // After body is injected: re-wire JS behaviour + intercept SPA links.
  useEffect(() => {
    if (!bodyHtml || !containerRef.current) return;
    const root = containerRef.current;

    // 1. Reveal-on-scroll IntersectionObserver
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.08 });
    root.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));

    // 2. Module-nav active state — track which section is in view
    const navItems = root.querySelectorAll('.module-nav-item');
    const sections = ['core', 'team', 'integrations'];
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          navItems.forEach(n => n.classList.remove('active'));
          const target = root.querySelector('.module-nav-item[href="#' + e.target.id + '"]');
          if (target) target.classList.add('active');
        }
      });
    }, { rootMargin: '-30% 0px -60% 0px' });
    sections.forEach(id => {
      const el = root.querySelector('#' + id);
      if (el) sectionObserver.observe(el);
    });

    // 3. Intercept internal links to use SPA navigation.
    const onLinkClick = (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      if (href.startsWith('mailto:') || href.startsWith('http')) return;
      if (href.startsWith('#')) return; // hash anchors handled by browser
      if (href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
      }
    };
    root.addEventListener('click', onLinkClick);

    // Mobile hamburger toggle
    const navEl = root.querySelector('nav');
    const burger = root.querySelector('.nav-burger');
    const onBurgerClick = (e) => {
      e.stopPropagation();
      if (!navEl) return;
      const open = navEl.classList.toggle('menu-open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    if (burger) burger.addEventListener('click', onBurgerClick);
    // Close menu after navigating
    const onAnyLink = () => {
      if (navEl) navEl.classList.remove('menu-open');
      if (burger) burger.setAttribute('aria-expanded', 'false');
    };
    root.addEventListener('click', onAnyLink, true);

    return () => {
      revealObserver.disconnect();
      sectionObserver.disconnect();
      root.removeEventListener('click', onLinkClick);
      root.removeEventListener('click', onAnyLink, true);
      if (burger) burger.removeEventListener('click', onBurgerClick);
    };
  }, [bodyHtml, navigate]);

  return (
    <>
      {styleHtml && <style dangerouslySetInnerHTML={{ __html: sanitizeStyleBlock(styleHtml) }} />}
      <div
        ref={containerRef}
        data-testid="marketing-features"
        dangerouslySetInnerHTML={{ __html: sanitizeMarketingHtml(bodyHtml) }}
      />
    </>
  );
}
